import { type QueryKey, useInfiniteQuery } from "@tanstack/react-query";
import {
	limit,
	makeOffsetInfiniteQueryOptions,
	type TotalCountMeta,
	useResetInfiniteData,
} from "web_myplann/Api/infiniteQueryUtils";
import type {
	ErrorResponse,
	FetchResponse,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponseQuery } from "web_myplann/Infrastructure/Api/ApiUtils";

type InfiniteListResponse<TPage extends TotalCountMeta> =
	| FetchResponse<TPage, 200>
	| FetchResponse<unknown, 400 | 401 | 403 | 500>
	| ErrorResponse;

type InfiniteListPage<TResponse extends InfiniteListResponse<TotalCountMeta>> =
	Extract<TResponse, { status: 200 }>["data"];

type Options = {
	pageSize?: number;
	enabled?: boolean;
};

export const useListInfiniteQuery = <
	TResponse extends InfiniteListResponse<TotalCountMeta>,
>(
	queryKey: QueryKey,
	fetchPage: (offset: number, pageSize: number) => Promise<TResponse>,
	{ pageSize = limit, enabled = true }: Options = {},
) => {
	useResetInfiniteData(queryKey, { disableReset: !enabled });

	return useInfiniteQuery({
		queryKey,
		queryFn: (ctx) =>
			processResponseQuery(() => fetchPage(ctx.pageParam, pageSize))(ctx),
		enabled,
		...makeOffsetInfiniteQueryOptions<InfiniteListPage<TResponse>>({
			pageSize,
		}),
	});
};
